import {AbortError} from 'src/errors/abort';
import {TimeoutError} from 'src/errors/timeout';
import {abortSignalAny, abortSignalTimeout} from 'src/utils/signals';

interface DeferredOptions {
  /**
   * Timeout in milliseconds for the deferred promise
   */
  timeoutMs?: number;
  /**
   * AbortSignal to manually abort the deferred promise
   */
  signal?: AbortSignal;
}

/**
 * Deferred is most useful when a Promise needs to be resolved in a different
 * context than it is constructed.
 */
export class Deferred<R = unknown> {
  public promise: Promise<R | undefined>;

  private _resolve: (result?: PromiseLike<R> | R) => void = () => {
    this._state = 'resolved';
  };

  private _reject: (error?: unknown) => void = () => {
    this._state = 'rejected';
  };

  private _state: 'pending' | 'resolved' | 'rejected' = 'pending';

  private done = false;

  constructor(private opts: DeferredOptions = {}) {
    const {timeoutMs = Number.MAX_SAFE_INTEGER, signal: userSignal} = this.opts;
    this._state = 'pending';
    this.promise = new Promise(
      (
        resolve: (result?: PromiseLike<R> | R) => void,
        reject: (error?: unknown) => void,
      ) => {
        this._resolve = resolve;
        this._reject = reject;
      },
    );

    this.promise
      .then(
        () => {
          this._state = 'resolved';
        },
        () => {
          this._state = 'rejected';
        },
      )
      .finally(() => {
        this.done = true;
      });

    const timeoutSignal = abortSignalTimeout(timeoutMs);
    const signals = [timeoutSignal];
    if (userSignal) {
      signals.push(userSignal);
    }
    const signal = abortSignalAny(signals);
    signal.addEventListener(
      'abort',
      () => {
        if (this._state === 'pending') {
          this.reject(this.getSignalAbortError(signal.reason));
        }
      },
      {once: true},
    );
  }

  resolve(result?: PromiseLike<R> | R): void {
    if (this.done) {
      return;
    }
    this._resolve(result);
  }

  reject(error: unknown): void {
    if (this.done) {
      return;
    }
    this._reject(error);
  }

  isResolved(): boolean {
    return this._state === 'resolved';
  }

  isRejected(): boolean {
    return this._state === 'rejected';
  }

  isPending(): boolean {
    return this._state === 'pending';
  }

  private getSignalAbortError(reason: unknown): Error {
    if (reason instanceof Error) {
      if (reason.name === 'TimeoutError') {
        return new TimeoutError(this.opts.timeoutMs ?? Number.MAX_SAFE_INTEGER);
      }
      return reason;
    }

    return new AbortError(reason);
  }
}
